<template>
  <div class="ops-page finance-page">
    <!-- Header -->
    <div class="ops-head">
      <div class="ops-head__left">
        <div class="ops-kicker">Ops Portal</div>
        <h2 class="ops-title">Finance</h2>
        <p class="ops-sub">
          Collect payments, tick milestones (50% → 40% → 10%), and automatically unlock Dispatch / Installer work.
        </p>
      </div>

      <div class="ops-head__right">
        <button class="btn btn--soft" @click="loadTasks" :disabled="loading">
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button class="tab" :class="tab==='tasks' ? 'active' : ''" @click="tab='tasks'">
        Tasks
        <span class="chip" :class="openTasks.length ? 'chip--amber' : 'chip--green'">{{ openTasks.length }}</span>
      </button>

      <button class="tab" :class="tab==='history' ? 'active' : ''" @click="tab='history'">
        History
        <span class="chip chip--muted">{{ recentDone.length }}</span>
      </button>
    </div>

    <!-- Filters -->
    <div class="filters" v-if="tab==='tasks'">
      <select v-model="milestone" class="inp" style="width:200px;">
        <option value="">All milestones</option>
        <option value="ADVANCE_50">Advance (50%)</option>
        <option value="SECOND_40">Second (40%)</option>
        <option value="FINAL_10">Final (10%)</option>
      </select>

      <input
        class="inp"
        v-model="q"
        placeholder="Search order id, customer, phone, lead code"
        style="margin-left:auto;max-width:420px;"
      >
    </div>

    <div class="ops-grid">
      <!-- LEFT: Tasks -->
      <div class="card" v-if="tab==='tasks'">
        <div class="card-head">
          <div>
            <div class="card-title">Open payment tasks</div>
            <div class="card-sub">
              Only unpaid milestones that match current stage gates appear here.
              <span class="hint">(BM approved → 50%, Dispatch done → 40%, Install done → 10%)</span>
            </div>
          </div>

          <div class="right">
            <span class="badge" :class="openTasks.length ? 'badge--amber' : 'badge--green'">
              Open: {{ openTasks.length }}
            </span>
          </div>
        </div>

        <div class="card-body">
          <div v-if="error" class="alert alert--danger">
            <div class="alert-title">Something went wrong</div>
            <div class="muted" style="margin-top:4px;">{{ error }}</div>
          </div>

          <div v-else-if="loading" class="muted">Loading…</div>

          <div v-else-if="filteredTasks.length===0" class="empty">
            <div class="empty-title">No payment tasks pending 🎉</div>
            <div class="empty-sub">Either everything is paid, or stages aren’t ready for the next payment gate yet.</div>
          </div>

          <div v-else class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th class="sort" @click="setSort('milestone')">
                    Milestone <span class="arrow">{{ sortArrow('milestone') }}</span>
                  </th>
                  <th class="sort" @click="setSort('amountDue')">
                    Amount <span class="arrow">{{ sortArrow('amountDue') }}</span>
                  </th>
                  <th class="sort" @click="setSort('orderId')">
                    Order <span class="arrow">{{ sortArrow('orderId') }}</span>
                  </th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Lead</th>
                  <th class="sort" @click="setSort('createdAt')">
                    Created <span class="arrow">{{ sortArrow('createdAt') }}</span>
                  </th>
                  <th style="width:280px;">Action</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="t in sortedTasks" :key="t.id" class="rowHover">
                  <td>
                    <span class="pillSmall" :class="milestoneClass(t.milestone)">
                      {{ milestoneLabel(t.milestone) }}
                    </span>
                  </td>

                  <td class="primaryCell">₹ {{ fmtMoney(t.amountDue) }}</td>

                  <td>
                    <span class="mono">#{{ t.orderId }}</span>
                    <div class="mini" v-if="t.stage">
                      <span class="pillTiny">{{ t.stage }}</span>
                    </div>
                  </td>

                  <td>{{ t.customerName || '—' }}</td>

                  <td><span class="mono">{{ t.customerPhone || '—' }}</span></td>

                  <td><span class="mono">{{ t.leadCode || t.inquiryCode || '—' }}</span></td>

                  <td>{{ fmtDT(t.createdAt) }}</td>

                  <td @click.stop="">
                    <div class="action-row">
                      <input
                        class="inp inp--note"
                        v-model="noteById[String(t.id)]"
                        placeholder="Note (optional) e.g., UPI ref / cash"
                      >

                      <button
                        class="btn btn--primary"
                        :disabled="acting[String(t.id)]"
                        @click.stop="markReceived(t)"
                        :title="'Mark ' + milestoneLabel(t.milestone) + ' received'"
                      >
                        {{ acting[String(t.id)] ? 'Saving…' : 'Payment received' }}
                      </button>

                      <button
                        class="btn btn--soft"
                        @click.stop="copyPayText(t)"
                        title="Copy WhatsApp-style follow-up"
                      >
                        Copy text
                      </button>
                    </div>

                    <div class="muted mini" style="margin-top:6px;">
                      Next step: <b>{{ nextUnlockText(t.milestone) }}</b>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="muted" style="margin-top:10px;">
              Tip: “Copy text” gives a clean follow-up you can paste to the client.
            </div>
          </div>
        </div>
      </div>

      <!-- RIGHT: History -->
      <div class="card" v-else>
        <div class="card-head">
          <div>
            <div class="card-title">Recent completions (local)</div>
            <div class="card-sub">Session-only log. If you want server history, we’ll add an endpoint later.</div>
          </div>
          <div class="right">
            <span class="badge" :class="recentDone.length ? 'badge--green' : 'badge--muted'">
              {{ recentDone.length ? 'Done: ' + recentDone.length : 'No items' }}
            </span>
          </div>
        </div>

        <div class="card-body">
          <div v-if="recentDone.length===0" class="empty">
            <div class="empty-title">No completions yet.</div>
            <div class="empty-sub">Mark a payment as received and it’ll show up here.</div>
          </div>

          <div v-else class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Milestone</th>
                  <th>Amount</th>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Lead</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="x in recentDone" :key="x._k" class="rowHover">
                  <td>{{ fmtDT(x._when) }}</td>
                  <td>
                    <span class="pillSmall" :class="milestoneClass(x.milestone)">
                      {{ milestoneLabel(x.milestone) }}
                    </span>
                  </td>
                  <td class="primaryCell">₹ {{ fmtMoney(x.amountDue) }}</td>
                  <td><span class="mono">#{{ x.orderId }}</span></td>
                  <td>{{ x.customerName || '—' }}</td>
                  <td><span class="mono">{{ x.leadCode || x.inquiryCode || '—' }}</span></td>
                  <td class="muted">{{ x.note || '—' }}</td>
                </tr>
              </tbody>
            </table>

            <div class="muted" style="margin-top:10px;">
              Note: This log is not persisted.
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'Finance',
  data () {
    return {
      tab: 'tasks',
      loading: false,
      error: '',
      q: '',
      milestone: '',
      tasks: [],
      acting: {},

      // optional note per payment id (sent to backend)
      noteById: {},

      // local-only completion log
      recentDone: [],

      sort: { key: 'createdAt', dir: 'asc' }
    }
  },

  computed: {
    user () { return this.$store.getters['auth/currentUser'] || {} },
    isFinance () { return this.user && (this.user.role === 'finance' || this.user.role === 'super_admin') },

    openTasks () {
      return Array.isArray(this.tasks) ? this.tasks : []
    },

    filteredTasks () {
      var list = Array.isArray(this.tasks) ? this.tasks : []
      var t = String(this.q || '').toLowerCase().trim()
      var ms = String(this.milestone || '').toUpperCase().trim()

      if (ms) {
        list = list.filter(function (x) {
          return String(x.milestone || '').toUpperCase() === ms
        })
      }

      if (!t) return list

      return list.filter(function (x) {
        var blob =
          String(x.id || '') + ' ' +
          String(x.orderId || '') + ' ' +
          String(x.milestone || '') + ' ' +
          String(x.amountDue || '') + ' ' +
          String(x.customerName || '') + ' ' +
          String(x.customerPhone || '') + ' ' +
          String(x.leadCode || '') + ' ' +
          String(x.inquiryCode || '') + ' ' +
          String(x.stage || '')
        return blob.toLowerCase().indexOf(t) > -1
      })
    },

    sortedTasks () {
      var arr = (this.filteredTasks || []).slice(0)
      var key = (this.sort && this.sort.key) ? this.sort.key : 'createdAt'
      var dir = (this.sort && this.sort.dir === 'desc') ? -1 : 1

      arr.sort(function (a, b) {
        var av = a ? a[key] : ''
        var bv = b ? b[key] : ''

        if (key === 'createdAt') {
          var ta = new Date(av || 0).getTime()
          var tb = new Date(bv || 0).getTime()
          return (ta - tb) * dir
        }
        if (key === 'milestone') {
          var rank = { ADVANCE_50: 1, SECOND_40: 2, FINAL_10: 3 }
          var ra = rank[String(av || '').toUpperCase()] || 99
          var rb = rank[String(bv || '').toUpperCase()] || 99
          return (ra - rb) * dir
        }
        if (key === 'amountDue' || key === 'orderId') {
          return (Number(av || 0) - Number(bv || 0)) * dir
        }
        return String(av || '').localeCompare(String(bv || '')) * dir
      })

      return arr
    }
  },

  created () {
    this.loadTasks()
  },

  methods: {
    async loadTasks () {
      this.error = ''
      if (!this.isFinance) {
        this.tasks = []
        this.error = 'You do not have access to Finance tasks.'
        return
      }

      this.loading = true
      try {
        var res = await this.$api.get('/finance/tasks')
        this.tasks = (res && Array.isArray(res.data)) ? res.data : []

      } catch (e) {
        console.error('Finance loadTasks failed', e)
        var msg = (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
          ? String(e.response.data.error || e.response.data.message)
          : (e && e.message ? String(e.message) : 'Could not load tasks. Please refresh.')
        this.error = msg
      } finally {
        this.loading = false
      }
    },

    async markReceived (t) {
      if (!t || !t.id) return
      var pid = String(t.id)
      if (this.acting[pid]) return

      this.$set(this.acting, pid, true)
      try {
        var note = this.noteById[pid]
        await this.$api.post('/order-payments/' + pid + '/received', { note: note || null })

        // local history (session only)
        this.recentDone.unshift({
          _k: pid + '-' + String(Date.now()),
          _when: new Date(),
          id: t.id,
          orderId: t.orderId,
          milestone: t.milestone,
          amountDue: t.amountDue,
          customerName: t.customerName,
          customerPhone: t.customerPhone,
          leadCode: t.leadCode,
          inquiryCode: t.inquiryCode,
          note: note || ''
        })

        // clear note for that id
        this.$set(this.noteById, pid, '')

        // reload from server (pipeline may have advanced + new tasks may appear)
        await this.loadTasks()
      } catch (e) {
        console.error('Finance markReceived failed', e)
        var msg = (e && e.response && e.response.data && (e.response.data.error || e.response.data.message))
          ? String(e.response.data.error || e.response.data.message)
          : 'Could not mark received. Please refresh and try again.'
        alert(msg)
      } finally {
        this.$set(this.acting, pid, false)
      }
    },

    nextUnlockText (milestone) {
      var ms = String(milestone || '').toUpperCase()
      if (ms === 'ADVANCE_50') return 'Unlocks Dispatch (DISPATCH_PENDING)'
      if (ms === 'SECOND_40') return 'Unlocks Installer (INSTALL_PENDING)'
      if (ms === 'FINAL_10') return 'Closes the order (CLOSED)'
      return '—'
    },

    copyPayText (t) {
      if (!t) return
      var ms = this.milestoneLabel(t.milestone)
      var order = '#' + String(t.orderId || '')
      var amt = '₹ ' + this.fmtMoney(t.amountDue)
      var name = String(t.customerName || 'Customer')
      var code = t.leadCode ? (' (' + String(t.leadCode) + ')') : (t.inquiryCode ? (' (' + String(t.inquiryCode) + ')') : '')
      var note = (this.noteById[String(t.id)] || '').trim()

      var lines = []
      lines.push('Hello ' + name + code + ',')
      lines.push('Payment reminder for ' + ms + ' of order ' + order + '.')
      lines.push('Amount due: ' + amt + '.')
      if (note) lines.push('Note: ' + note)
      lines.push('Please confirm once done. धन्यवाद 🙏')

      this.copyToClipboard(lines.join('\n'))
    },

    async copyToClipboard (text) {
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(String(text || ''))
          alert('Copied.')
          return
        }
      } catch (_) {}

      try {
        var ta = document.createElement('textarea')
        ta.value = String(text || '')
        ta.setAttribute('readonly', '')
        ta.style.position = 'absolute'
        ta.style.left = '-9999px'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        alert('Copied.')
      } catch (e) {
        alert('Could not copy on this browser.')
      }
    },

    setSort (key) {
      var s = this.sort || { key: 'createdAt', dir: 'asc' }
      if (s.key === key) s.dir = (s.dir === 'asc') ? 'desc' : 'asc'
      else { s.key = key; s.dir = 'asc' }
      this.sort = { key: s.key, dir: s.dir }
    },

    sortArrow (key) {
      var s = this.sort || {}
      if (s.key !== key) return ''
      return s.dir === 'asc' ? '▲' : '▼'
    },

    milestoneLabel (m) {
      var x = String(m || '').toUpperCase()
      if (x === 'ADVANCE_50') return 'Advance (50%)'
      if (x === 'SECOND_40') return 'Second (40%)'
      if (x === 'FINAL_10') return 'Final (10%)'
      return x || '—'
    },

    milestoneClass (m) {
      var x = String(m || '').toUpperCase()
      if (x === 'ADVANCE_50') return 'ok'
      if (x === 'SECOND_40') return 'warn'
      if (x === 'FINAL_10') return 'bad'
      return 'muted'
    },

    fmtDT (d) { return d ? dayjs(d).format('YYYY-MM-DD HH:mm') : '—' },

    fmtMoney (v) {
      var n = Number(v || 0)
      try { return n.toLocaleString('en-IN') } catch (_) { return String(n) }
    }
  }
}
</script>

<style scoped>
.ops-page { padding: 18px; background:#fafafa; }

.ops-head { display:flex; justify-content:space-between; gap:14px; align-items:flex-end; margin-bottom:14px; }
.ops-kicker { font-size:12px; opacity:.7; }
.ops-title { margin:0; font-size:22px; font-weight:900; color:#111827; }
.ops-sub { margin:6px 0 0; opacity:.75; font-size:13px; max-width:760px; color:#374151; }

.ops-grid { display:grid; grid-template-columns: 1fr; gap:14px; }

.tabs { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:10px; }
.tab{
  border:1px solid #e5e7eb;
  background:#fff;
  color:#111827;
  border-radius:999px;
  padding:8px 12px;
  cursor:pointer;
  font-weight:800;
  display:flex;
  align-items:center;
  gap:8px;
}
.tab.active{ border-color:#111827; background:#111827; color:#fff; }

.chip{
  font-size:12px;
  border-radius:999px;
  padding:2px 8px;
  border:1px solid #e5e7eb;
  font-weight:900;
}
.chip--green{ background:#14532d; color:#dcfce7; border-color:#14532d; }
.chip--amber{ background:#92400e; color:#fff7ed; border-color:#92400e; }
.chip--muted{ background:#f3f4f6; color:#111827; border-color:#e5e7eb; }

.filters{ display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin-bottom:12px; }

.inp {
  border:1px solid rgba(0,0,0,.12);
  border-radius:10px;
  padding:10px 10px;
  font-size:13px;
  outline:none;
  background:#fff;
}
.inp--note{ padding:9px 10px; min-width: 180px; }

.btn { border:1px solid rgba(0,0,0,.12); background:#fff; padding:10px 12px; border-radius:10px; cursor:pointer; font-size:13px; font-weight:800; }
.btn:disabled { opacity:.55; cursor:not-allowed; }
.btn--soft { background: rgba(0,0,0,.03); }
.btn--primary { background:#111827; color:#fff; border-color:#111827; }

.card { background:#fff; border:1px solid rgba(0,0,0,.08); border-radius:14px; box-shadow:0 10px 28px rgba(17,24,39,0.06); overflow:hidden; }
.card-head { padding:14px; border-bottom:1px solid rgba(0,0,0,.06); display:flex; justify-content:space-between; gap:12px; align-items:flex-start; }
.card-title { font-weight:900; font-size:15px; color:#111827; }
.card-sub { font-size:12px; opacity:.75; margin-top:4px; color:#6b7280; max-width:860px; }
.hint{ opacity:.75; margin-left:6px; }
.card-body { padding: 12px 14px; }

.badge{
  padding:4px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  background:#f3f4f6;
  font-weight:900;
}
.badge--green{ background:#14532d;color:#dcfce7;border-color:#14532d }
.badge--amber{ background:#92400e;color:#fff7ed;border-color:#92400e }
.badge--muted{ background:#f3f4f6;color:#111827;border-color:#e5e7eb }

.alert { margin:12px 0; padding:10px 12px; border-radius:12px; font-size:13px; }
.alert--danger { background:#fff1f2; border:1px solid #fecdd3; color:#9f1239; }
.alert-title{ font-weight:900; }

.table-wrap{ overflow:auto; }
.table { width: 100%; border-collapse: collapse; min-width: 1040px; }
.table th, .table td {
  text-align: left;
  padding: 10px 8px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 13px;
  vertical-align: top;
}
.table th.sort{ cursor:pointer; user-select:none; }
.arrow{ font-size:11px; margin-left:6px; color:#9ca3af; }
.rowHover:hover{ background:#fafafa; }
.primaryCell{ font-weight:900; color:#111827; }

.pillSmall{
  display:inline-block;
  padding:2px 10px;
  border-radius:999px;
  font-size:12px;
  border:1px solid #e5e7eb;
  font-weight:900;
}
.pillSmall.ok{ background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
.pillSmall.warn{ background:#fff7ed; border-color:#fdba74; color:#9a3412; }
.pillSmall.bad{ background:#fff1f2; border-color:#fecdd3; color:#9f1239; }
.pillSmall.muted{ background:#f3f4f6; border-color:#e5e7eb; color:#111827; }

.pillTiny{
  display:inline-block;
  padding:2px 8px;
  border-radius:999px;
  font-size:11px;
  background: rgba(0,0,0,.05);
  border:1px solid rgba(0,0,0,.08);
  margin-top:6px;
}

.action-row{ display:flex; gap:8px; align-items:center; flex-wrap:wrap; }

.muted { color:#6b7280; }
.mini{ font-size:12px; }
.empty{ padding:16px 6px; text-align:center; color:#6b7280; }
.empty-title{ font-weight:900; color:#111827; }
.empty-sub{ margin-top:6px; font-size:13px; opacity:.8; }

.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
}
</style>
